import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';

import formats from '@/assets/translationFiles/formats.json';

// Lazy per-language loaders. Each dynamic import becomes its own esbuild chunk,
// so only the active language (~180 KB) is downloaded at startup instead of all
// nine (~1.6 MB) being bundled into the entry that blocks first render. The rest
// load on demand when the user switches language.
const resourceLoaders = {
  EN: () => import('@/assets/translationFiles/EN.json'),
  FR: () => import('@/assets/translationFiles/FR.json'),
  ES: () => import('@/assets/translationFiles/ES.json'),
  DE: () => import('@/assets/translationFiles/DE.json'),
  IT: () => import('@/assets/translationFiles/IT.json'),
  NL: () => import('@/assets/translationFiles/NL.json'),
  SV: () => import('@/assets/translationFiles/SV.json'),
  CS: () => import('@/assets/translationFiles/CS.json'),
  PL: () => import('@/assets/translationFiles/PL.json'),
};

// Detect the initial language the same way index.html's loader script does:
// ?lang= query param, then localStorage 'locale', defaulting to EN.
const detectInitialLanguage = () => {
  const search = window.location.href.replace('/#', '').split('?')[1] || '';
  const fromQuery = new URLSearchParams(search).get('lang');
  const lng = (fromQuery || localStorage.getItem('locale') || 'EN').toUpperCase();
  return resourceLoaders[lng] ? lng : 'EN';
};

// Custom i18next backend: dynamically imports one bundled language at a time and
// merges in the shared formats. changeLanguage() awaits read() automatically, so
// existing `await i18n.changeLanguage(locale)` callers transparently wait for the
// chunk to load.
const lazyBundledBackend = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    const loader = resourceLoaders[language];
    if (!loader) return callback(null, {});
    loader()
      .then((mod) => callback(null, { ...formats?.formats, ...(mod.default ?? mod) }))
      .catch((error) => callback(error, false));
  },
};

const takeFilesFromBE = localStorage.getItem('translationsBE');
i18n.use(takeFilesFromBE ? HttpBackend : lazyBundledBackend).init({
  ...(takeFilesFromBE
    ? {
        backend: {
          loadPath: '/v1/public/translation/{{lng}}',
          parse: (data, lng) => {
            const env = window.location.hostname.split('.')[0];
            const isNotProd = ['localhost', 'dev', 'sandbox'].includes(env);

            const { keys, ...rest } = JSON.parse(data).translation;
            let allValues = {};
            Object.values(rest).forEach((item) => {
              const temp = {};
              Object.entries(item).forEach(([key, value]) => {
                temp[key] = value ? value : `${isNotProd && lng !== 'EN' ? `(${lng})` : ''}${key}`;
              });
              allValues = { ...allValues, ...temp };
            });
            return { ...formats?.formats, ...allValues };
          },
        },
        load: 'languageOnly',
        preload: ['EN', 'FR', 'DE', 'ES', 'IT', 'NL', 'PL', 'SV', 'CS'],
      }
    : {
        lng: detectInitialLanguage(),
        partialBundledLanguages: true,
      }),
  load: 'languageOnly',
  keySeparator: false,
  nsSeparator: '::',
  interpolation: {
    escapeValue: false,
    format(value, format, lng) {
      switch (format) {
        case 'date':
          if (Object.hasOwn(value, 'date') && Object.hasOwn(value, 'timeZone')) {
            return new Intl.DateTimeFormat(lng, {
              timeZone: value.timeZone,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }).format(value.date);
          }
          break;
        case 'longDate':
          if (Object.hasOwn(value, 'date') && Object.hasOwn(value, 'timeZone')) {
            return new Intl.DateTimeFormat(lng, {
              timeZone: value.timeZone,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }).format(value.date);
          }
          break;
        case 'longDateTime':
          if (Object.hasOwn(value, 'date') && Object.hasOwn(value, 'timeZone')) {
            return new Intl.DateTimeFormat(lng, {
              timeZone: value.timeZone,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            }).format(value.date);
          }
          break;
        case 'number':
          if (!Number.isNaN(Number(value))) {
            return new Intl.NumberFormat(lng).format(value);
          }
          if (Object.hasOwn(value, 'number') && Object.hasOwn(value, 'currency')) {
            return new Intl.NumberFormat(lng, {
              style: 'currency',
              currency: value.currency,
            }).format(value.number);
          }
          if (Object.hasOwn(value, 'number')) {
            return new Intl.NumberFormat(lng, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value.number);
          }
          break;

        case 'time':
          if (Object.hasOwn(value, 'time') && Object.hasOwn(value, 'timeZone')) {
            return new Intl.DateTimeFormat(lng, {
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
              timeZone: value.timeZone,
              timeZoneName: 'short',
            }).format(value.time);
          }
          break;
        default:
          return value;
      }
    },
  }, // React already does escaping
  react: {
    transKeepBasicHtmlNodesFor: [
      'br',
      'strong',
      'i',
      'u',
      'p',
      'h2',
      'a',
      'h3',
      'h4',
      'ul',
      'li',
      'pre',
      'table',
      'tr',
      'th',
      'td',
    ],
  },
  fallbackLng: false,
});

export default i18n;
